import React, { useState, useRef, useEffect, useReducer, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Table, Badge, Button, Form, Row, Col, Spinner } from 'react-bootstrap';
import { Upload, Plus, Trash2, Save, FileCheck, AlertCircle } from 'lucide-react';
import { getMedicines } from '../../../Services/Service';

// --- Global Shared Cache (Singleton) ---
const SEARCH_CACHE = new Map();

// --- Constants & Config ---
const CONSTANTS = {
    STATUS_COLORS: {
        missing: 'danger',
        pending: 'warning',
        uploaded: 'success',
        default: 'secondary'
    },
    EMPTY_DATE: '0000-00-00',
    DATE_OPTS: { day: '2-digit', month: 'short', year: 'numeric' }
};

// --- Utilities ---
const generateId = () => crypto.randomUUID ? crypto.randomUUID() : `med_${Date.now()}_${Math.random().toString(36).slice(2)}`;

const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? 'N/A' : new Intl.DateTimeFormat('en-GB', CONSTANTS.DATE_OPTS).format(date);
};

const validateMedicine = (med) => {
    const errors = {};
    if (!med.name?.trim()) errors.name = true;
    if (!med.dosage) errors.dosage = true;
    if (!med.frequency) errors.frequency = true;
    if (!med.startDate) errors.startDate = true;
    if (!med.endDate) errors.endDate = true;
    return errors;
};

// --- Reducer ---
const INITIAL_STATE = {
    medicines: [],
    selectedFile: null,
    errors: {}, 
    isRestrictedMode: false
};

const patientReducer = (state, action) => {
    switch (action.type) {
        case 'INIT':
            return {
                ...state,
                medicines: action.payload.medicines || [],
                isRestrictedMode: action.payload.isRestrictedMode
            };
        case 'ADD_MED':
            return {
                ...state,
                medicines: [...state.medicines, { id: generateId(), name: '', dosage: '', frequency: '', startDate: '', endDate: '', notes: '' }]
            };
        case 'REMOVE_MED':
            const newErrorsRemove = { ...state.errors };
            delete newErrorsRemove[action.id];
            return {
                ...state,
                medicines: state.medicines.filter(m => m.id !== action.id),
                errors: newErrorsRemove
            };
        case 'UPDATE_MED':
            const { id, field, value } = action.payload;
            const updatedMeds = state.medicines.map(m => m.id === id ? { ...m, [field]: value } : m);
            
            let updatedErrors = state.errors;
            if (state.errors[id] && state.errors[id][field]) {
                updatedErrors = { ...state.errors, [id]: { ...state.errors[id] } };
                delete updatedErrors[id][field];
                if (Object.keys(updatedErrors[id]).length === 0) delete updatedErrors[id];
            }

            return { ...state, medicines: updatedMeds, errors: updatedErrors };
        case 'SET_FILE':
            return { ...state, selectedFile: action.file };
        case 'VALIDATE_ALL':
            const validationErrors = {};
            let isValid = true;
            state.medicines.forEach(med => {
                const rowErrors = validateMedicine(med);
                if (Object.keys(rowErrors).length > 0) {
                    validationErrors[med.id] = rowErrors;
                    isValid = false;
                }
            });
            return { ...state, errors: validationErrors, isValid }; 
        default:
            return state;
    }
};

// --- Components ---

const DropdownPortal = ({ targetRef, show, children, onDismiss }) => {
    if (!show || !targetRef.current) return null;

    const rect = targetRef.current.getBoundingClientRect();
    const style = {
        position: 'fixed',
        top: `${rect.bottom}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        zIndex: 9999,
        maxHeight: '200px',
        overflowY: 'auto'
    };

    return ReactDOM.createPortal(
        <>
            <div className="position-fixed top-0 start-0 w-100 h-100 bg-transparent" onMouseDown={onDismiss} style={{ zIndex: 9998 }} />
            <div className="bg-white border shadow-sm rounded" style={style}>
                {children}
            </div>
        </>,
        document.body
    );
};

const MedicineSearchInput = ({ value, onChange, isInvalid }) => {
    const [results, setResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const inputRef = useRef(null);
    const abortRef = useRef(null);

    const handleSearch = useCallback(async (term) => {
        const normalized = term.trim().toLowerCase();
        if (!normalized) return setResults([]);

        if (SEARCH_CACHE.has(normalized)) {
            setResults(SEARCH_CACHE.get(normalized));
            return;
        }

        if (abortRef.current) abortRef.current.abort();
        abortRef.current = new AbortController();

        setIsLoading(true);
        try {
            const res = await getMedicines(1, 50, term, { signal: abortRef.current.signal });
            if (res?.data?.status === 'success') {
                const data = res.data.data || [];
                SEARCH_CACHE.set(normalized, data);
                setResults(data);
            }
        } catch (error) {
            if (error.name !== 'AbortError') console.error(error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const onInputChange = (e) => {
        const val = e.target.value;
        onChange(val);
        setShowDropdown(!!val.trim());
        handleSearch(val); 
    };

    return (
        <>
            <div className="position-relative">
                <Form.Control
                    ref={inputRef}
                    size="sm"
                    value={value}
                    isInvalid={isInvalid}
                    onChange={onInputChange}
                    onFocus={() => value.trim() && setShowDropdown(true)}
                    placeholder="Search..."
                    autoComplete="off"
                />
                {isLoading && <Spinner animation="border" size="sm" className="position-absolute end-0 top-50 translate-middle-y me-2" />}
            </div>
            
            <DropdownPortal targetRef={inputRef} show={showDropdown && results.length > 0} onDismiss={() => setShowDropdown(false)}>
                {results.map(item => (
                    <div 
                        key={item.id} 
                        className="px-3 py-2 text-truncate small"
                        style={{ cursor: 'pointer' }}
                        onMouseDown={() => { onChange(item.medicine_name); setShowDropdown(false); }}
                        onMouseOver={e => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                        onMouseOut={e => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                        {item.medicine_name}
                    </div>
                ))}
            </DropdownPortal>
        </>
    );
};

const MedicineRow = React.memo(({ med, dispatch, errors }) => {
    const update = (field, value) => dispatch({ type: 'UPDATE_MED', payload: { id: med.id, field, value } });

    return (
        <Row className="g-2 mb-2 align-items-end">
            <Col md={3}>
                <Form.Label className="x-small mb-0 text-muted">Medicine*</Form.Label>
                <MedicineSearchInput value={med.name} onChange={v => update('name', v)} isInvalid={!!errors?.name} />
            </Col>
            <Col md={1}>
                <Form.Label className="x-small mb-0 text-muted">Dosage*</Form.Label>
                <Form.Select size="sm" value={med.dosage} isInvalid={!!errors?.dosage} onChange={e => update('dosage', e.target.value)}>
                    <option value="">Select</option>
                    {['0.25', '0.5', '1', '1.25', '1.5', '2'].map(v => <option key={v} value={v}>{v} mg</option>)}
                </Form.Select>
            </Col>
            <Col md={1}>
                <Form.Label className="x-small mb-0 text-muted">Frequency*</Form.Label>
                <Form.Select size="sm" value={med.frequency} isInvalid={!!errors?.frequency} onChange={e => update('frequency', e.target.value)}>
                    <option value="">Select</option>
                    {['Once Daily', 'Twice Daily', 'Thrice Daily', 'Once Weekly'].map(v => <option key={v} value={v}>{v}</option>)}
                </Form.Select>
            </Col>
            <Col md={2}>
                <Form.Label className="x-small mb-0 text-muted">Start Date*</Form.Label>
                <Form.Control type="date" size="sm" value={med.startDate} isInvalid={!!errors?.startDate} onChange={e => update('startDate', e.target.value)} />
            </Col>
            <Col md={2}>
                <Form.Label className="x-small mb-0 text-muted">End Date*</Form.Label>
                <Form.Control type="date" size="sm" value={med.endDate} isInvalid={!!errors?.endDate} onChange={e => update('endDate', e.target.value)} />
            </Col>
            <Col md={2}>
                <Form.Label className="x-small mb-0 text-muted">Medication Notes (optional)</Form.Label>
                <Form.Control as="textarea" rows={1} size="sm" value={med.notes} onChange={e => update('notes', e.target.value)} />
            </Col>
            <Col md={1} className="text-end">
                <Button variant="link" size="sm" className="text-danger p-0 mb-1" onClick={() => dispatch({ type: 'REMOVE_MED', id: med.id })}>
                    <Trash2 size={16} />
                </Button>
            </Col>
        </Row>
    );
});

const PatientRow = ({ patient, onDNA, onDateUpdate, onSave ,onDischarge }) => {
    const isFirstAppt = patient.first_appointment_date;
    
    // --- UPDATED LOGIC ---
    // Strictly follow the isFirstAppt flag. 
    // Even if the API sends a default 'first_consultation_date' (like registration date),
    // if the flag is 1, we treat it as restricted mode (Save Date only).
    const isRestricted = !!isFirstAppt;

    const [state, dispatch] = useReducer(patientReducer, INITIAL_STATE);
    const fileInputRef = useRef(null);

    useEffect(() => {
        dispatch({ type: 'INIT', payload: { medicines: patient.medicines || [], isRestrictedMode: isRestricted } });
    }, [patient.id, isRestricted]);

    const handleSave = () => {
        if (state.isRestrictedMode) {
            onSave(patient.id, { isFirstAppointment: true });
            return;
        }

        const result = patientReducer(state, { type: 'VALIDATE_ALL' });
        if (!result.isValid) {
            dispatch({ type: 'VALIDATE_ALL' }); 
            return;
        }
        onSave(patient.id, { medicines: state.medicines, selectedFile: state.selectedFile });
    };

    const getBadge = () => {
        if (isFirstAppt) return <Badge bg="primary">New</Badge>;
        return <Badge bg={CONSTANTS.STATUS_COLORS[patient.reportStatus] || 'secondary'}>{patient.reportStatus}</Badge>;
    };

    return (
        <React.Fragment>
            <tr className="border-bottom">
                <td className="fw-semibold text-primary">{patient.patientName}</td>
                <td><span className="text-muted">{patient.nhsId}</span></td>
                <td>{isFirstAppt ? <span className="text-muted">-</span> : formatDate(patient.registrationDate)}</td>
                <td>{getBadge()}</td>
                
                <td>
                    {state.isRestrictedMode ? <span className="text-muted small">Not Required</span> : (
                        <>
                            <input type="file" ref={fileInputRef} hidden onChange={e => dispatch({ type: 'SET_FILE', file: e.target.files[0] })} />
                            <Button variant={state.selectedFile || patient.reportStatus === 'uploaded' ? 'success' : 'outline-primary'} size="sm" onClick={() => fileInputRef.current.click()}>
                                {state.selectedFile ? <FileCheck size={14} className="me-1"/> : <Upload size={14} className="me-1"/>}
                                {state.selectedFile ? 'Selected' : 'Select File'}
                            </Button>
                            {state.selectedFile && <div className="x-small text-success mt-1 text-truncate" style={{maxWidth: '100px'}}>{state.selectedFile.name}</div>}
                        </>
                    )}
                </td>

                <td>{isFirstAppt ? <Form.Control type="date" size="sm" value={patient.nextConsultationDate || ''} onChange={e => onDateUpdate(patient.id, e.target.value)} /> : <span className="text-muted text-center d-block">-</span>}</td>
                <td>{!isFirstAppt ? <Form.Control type="date" size="sm" value={patient.nextConsultationDate || ''} onChange={e => onDateUpdate(patient.id, e.target.value)} /> : <span className="text-muted text-center d-block">-</span>}</td>

                <td>
                    {state.isRestrictedMode ? (
                        <Button variant="success" size="sm" className="w-100" onClick={handleSave}><Save size={14} className="me-1"/> Save Date</Button>
                    ) : (
                        <div className="d-flex gap-2">
                            <Button variant="outline-primary" size="sm" onClick={() => dispatch({ type: 'ADD_MED' })}><Plus size={14} className="me-1"/> Add Medicine</Button>
                            <Button variant="outline-danger" size="sm" onClick={() => onDNA(patient)}>DNA</Button>
                            <Button variant="outline-success" size="sm" onClick={handleSave}>Save</Button>
                            <Button variant="outline-warning" size="sm" onClick={() => onDischarge(patient)}>Discharge</Button>
                        </div>
                    )}
                    {Object.keys(state.errors).length > 0 && <div className="text-danger x-small mt-1"><AlertCircle size={12}/> Check highlighted fields</div>}
                </td>
            </tr>

            {!state.isRestrictedMode && state.medicines.length > 0 && (
                <tr className="bg-light">
                    <td colSpan={8} className="px-4 py-2">
                        {state.medicines.map(med => (
                            <MedicineRow key={med.id} med={med} dispatch={dispatch} errors={state.errors[med.id]} />
                        ))}
                    </td>
                </tr>
            )}
            {!state.isRestrictedMode && state.medicines.length === 0 && (
                <tr className="bg-light"><td colSpan={8} className="text-center text-muted py-2 small">No medicines added.</td></tr>
            )}
        </React.Fragment>
    );
};

const PatientTable = ({ patients, onDNA, onDateUpdate, onSave,onDischarge  }) => (
    <div className="table-responsive" style={{ minHeight: '400px', paddingBottom: '80px' }}>
        <Table className="patient-table mb-0" hover>
            <thead>
                <tr>
                    <th>Patient Name</th><th>NHS ID</th><th>Last Consultation</th><th>Status</th><th>Doctor notes</th><th>First Consultation</th><th>Next Consultation</th><th>Actions</th>
                </tr>
            </thead>
            <tbody>
                {patients.map(p => <PatientRow key={p.id} patient={p} onDNA={onDNA} onDateUpdate={onDateUpdate} onSave={onSave} onDischarge={onDischarge} />)}
            </tbody>
        </Table>
    </div>
);

export default PatientTable;